import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {authApi} from '../../api/auth.api';
import {Spinner, PasswordStrengthMeter} from '../../components/ui/index';
import OtpInput from 'react-otp-input';
import { toPersian } from '../../utils/persianNumber';
import { useAuth } from '../../store/AuthContext';
import { DeviceMobile, LockKey } from '@phosphor-icons/react';
import { isValidPhone, normalizeIranianMobile, phoneInputChange } from '../../utils/validators';


export function RegisterPhonePage() {

  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, loadProfile, getDashboardRoute } = useAuth();
  const username = location.state?.username || '';
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(0);


  const CODE_LEN = 7
  // Signup already triggers one OTP SMS server-side (views.signup calls
  // generateUserPhoneAuthenticationCode before redirecting here) - auto-
  // sending another on mount used to overwrite that code with a second one,
  // so only the second SMS's code still validated. Just start the resend
  // countdown so the user isn't immediately allowed to spam "ارسال مجدد".
  useEffect(() => {
    if (username) setCountdown(90);
  }, []);

  const sendCode = async () => {
    if (countdown > 0) return;
    setSending(true); setError('');
    try {
      await authApi.generateAuthCode(username, 'phone');
      setCountdown(90);
    } catch {
      setError('خطا در ارسال کد. دوباره تلاش کنید.');
    }
    setSending(false);
  };

  // countdown timer
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [countdown]);

  const verify = async () => {
    if (code.length < CODE_LEN) { setError('کد 7 رقمی را کامل وارد کنید.'); return; }
    setLoading(true); setError('');
    try {
      const res = await authApi.registerInfo({
        auth_code: code,
        type: 'phone',
        self_registration: 1,
        username,
      });
      if (res.data === 'OK' || res.status === 200) {
        setSuccess(true);
        // Phone verification finalizes the account server-side, but
        // AuthContext's `user` is still whatever it was before this whole
        // signup flow started (null, if this is a fresh signup) - just
        // navigating to '/' without refreshing it left the app thinking
        // nobody was logged in, so it kept showing the landing/login page
        // instead of the dashboard even though a real session now exists.
        const profile = await loadProfile();
        setTimeout(() => {
          if (profile) { setUser(profile); navigate(getDashboardRoute(profile.type)); }
          else navigate('/');
        }, 1500);
      } else {
        setError('کد وارد شده صحیح نیست.');
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 400) setError('کد وارد شده صحیح یا منقضی شده است.');
      else if (!err.response) setError('اتصال به سرور برقرار نشد.');
      else setError(`خطا در تأیید کد (${status}).`);
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, position: 'relative', zIndex: 1 }}>
      <div style={{ background: '#fff', border: '1px solid rgba(35,58,122,0.12)', borderRadius: 20, padding: 36, boxShadow: '0 20px 60px rgba(35,58,122,0.12)', maxWidth: 420, width: '100%', textAlign: 'center' }}>
        
        <div style={{ marginBottom: 16, color: 'var(--blue-600, #233A7A)' }}><DeviceMobile size={48} weight="regular" /></div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>تأیید شماره موبایل</h2>
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 32 }}>
          کد ۷ رقمی ارسال‌شده به موبایل شما را وارد کنید
        </p>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#dc2626', fontSize: 13 }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#15803d', fontSize: 13 }}>
            ✓ تأیید شد! در حال انتقال...
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); verify(); }}>
        <div style={{ marginBottom: 28, background: '#fff', opacity: 1 }}>
          <OtpInput
            value={code}
            onChange={setCode}
            numInputs={CODE_LEN}
            shouldAutoFocus
            renderInput={(props) => <input {...props} disabled={loading || success} />}
            inputStyle={{ width: '2.2em', height: '2.4em', margin: '0 4px', fontSize: 18, borderRadius: 8, border: '1px solid #e2e8f0', textAlign: 'center', background: '#fff', backgroundColor: '#fff', opacity: 1, color: '#0f172a' }}
            containerStyle={{ direction: 'ltr', background: '#fff', opacity: 1 }}
          />
        </div>

        <button
          type="submit"
          disabled={loading || success || code.length < CODE_LEN}
          style={{ width: '100%', padding: '12px', borderRadius: 10, background: 'linear-gradient(135deg,#29AAE1,#233A7A)', color: '#fff', fontSize: 14, fontWeight: 700, border: 'none', cursor: code.length < CODE_LEN ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 16, opacity: code.length < CODE_LEN ? 0.6 : 1 }}
        >
          {loading ? <><Spinner size={14} color="#fff" /> در حال تأیید...</> : 'تأیید کد'}
        </button>
        </form>

        {/* ارسال مجدد با countdown */}
        <button
          type="button"
          onClick={sendCode}
          disabled={countdown > 0 || sending}
          style={{ background: 'none', border: 'none', fontSize: 13, cursor: countdown > 0 ? 'default' : 'pointer', fontFamily: 'inherit', color: countdown > 0 ? '#94a3b8' : '#29AAE1' }}
        >
          {sending ? 'در حال ارسال...' : countdown > 0 ? `ارسال مجدد تا ${toPersian(countdown)} ثانیه دیگر` : 'ارسال مجدد کد'}
        </button>
      </div>
    </div>
  );
}


export const ForgetPasswordPage = () => {
  const navigate = useNavigate();
  const CODE_LEN = 7;

  const [step, setStep] = useState('request'); // 'request' | 'verify'
  const [username, setUsername] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  // Only start showing the validity message once the user has actually
  // typed a digit - an empty field shouldn't scream "invalid" before
  // they've had a chance to type anything.
  const phoneTouched = phoneNumber.trim().length > 0;
  const phoneValid = isValidPhone(phoneNumber);
  const phoneError = phoneTouched && !phoneValid ? 'شماره موبایل معتبر نیست (مثال: 09123456789)' : '';
  const [code, setCode] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const passwordsMismatch = confirmPass.length > 0 && newPass !== confirmPass;
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [countdown]);

  const sendCode = async () => {
    if (!username.trim() || !phoneNumber.trim()) {
      setError('نام کاربری و شماره موبایل را وارد کنید.');
      return;
    }
    if (!phoneValid) {
      setError('شماره موبایل معتبر نیست (مثال: 09123456789)');
      return;
    }
    setSending(true); setError('');
    try {
      await authApi.generateAuthCode(username.trim(), 'phone', normalizeIranianMobile(phoneNumber));
      setStep('verify');
      setCountdown(90);
    } catch (err) {
      setError('خطا در ارسال کد. نام کاربری یا شماره موبایل را بررسی کنید.');
    }
    setSending(false);
  };

  const resendCode = async () => {
    if (countdown > 0) return;
    setSending(true); setError('');
    try {
      await authApi.generateAuthCode(username.trim(), 'phone', normalizeIranianMobile(phoneNumber));
      setCountdown(90);
    } catch {
      setError('خطا در ارسال کد. دوباره تلاش کنید.');
    }
    setSending(false);
  };

  const submitReset = async () => {
    if (code.length < CODE_LEN) { setError('کد را کامل وارد کنید.'); return; }
    if (newPass.length < 6) { setError('رمز عبور باید حداقل ۶ کاراکتر باشد.'); return; }
    if (newPass !== confirmPass) { setError('رمز عبور و تکرار آن یکسان نیستند.'); return; }

    setLoading(true); setError('');
    try {
      await authApi.resetPassword({
        username: username.trim(),
        auth_code: code,
        new_pass: newPass,
        type: 'phone',
      });
      setSuccess(true);
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) setError('کد وارد شده صحیح یا منقضی شده است.');
      else if (!err.response) setError('اتصال به سرور برقرار نشد.');
      else setError(`خطا در بازنشانی رمز عبور (${status}).`);
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, position: 'relative', zIndex: 1 }}>
      <div style={{ background: '#fff', border: '1px solid rgba(35,58,122,0.12)', borderRadius: 20, padding: 36, boxShadow: '0 20px 60px rgba(35,58,122,0.12)', maxWidth: 420, width: '100%', textAlign: 'center' }}>

        <div style={{ marginBottom: 16, color: 'var(--blue-600, #233A7A)' }}><LockKey size={48} weight="regular" /></div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>بازیابی رمز عبور</h2>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#dc2626', fontSize: 13 }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#15803d', fontSize: 13 }}>
            ✓ رمز عبور با موفقیت تغییر کرد! در حال انتقال...
          </div>
        )}

        {step === 'request' && !success && (
          <form onSubmit={(e) => { e.preventDefault(); sendCode(); }}>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 24 }}>
              نام کاربری و شماره موبایل ثبت‌شده خود را وارد کنید
            </p>
            <input
              type="text"
              placeholder="نام کاربری"
              value={username}
              autoFocus
              dir="ltr"
              className="pj-ltr-field"
              onChange={(e) => setUsername(e.target.value)}
              style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 14, marginBottom: 12, fontFamily: 'inherit', boxSizing: 'border-box' }}
            />
            <input
              type="tel"
              placeholder="شماره موبایل (09xxxxxxxxx)"
              dir="ltr"
              value={phoneNumber}
              onChange={phoneInputChange((e) => setPhoneNumber(e.target.value))}
              style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: `1px solid ${phoneError ? '#fca5a5' : '#e2e8f0'}`, fontSize: 14, marginBottom: phoneError ? 6 : 20, fontFamily: 'inherit', boxSizing: 'border-box', textAlign: 'center' }}
            />
            {phoneError && (
              <p style={{ color: '#dc2626', fontSize: 12, textAlign: 'right', margin: '0 0 14px' }}>{phoneError}</p>
            )}
            <button
              type="submit"
              disabled={sending || !username.trim() || !phoneValid}
              style={{ width: '100%', padding: '12px', borderRadius: 10, background: 'linear-gradient(135deg,#29AAE1,#233A7A)', color: '#fff', fontSize: 14, fontWeight: 700, border: 'none', cursor: (sending || !username.trim() || !phoneValid) ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: (sending || !username.trim() || !phoneValid) ? 0.6 : 1 }}
            >
              {sending ? <><Spinner size={14} color="#fff" /> در حال ارسال...</> : 'ارسال کد تایید'}
            </button>
          </form>
        )}

        {step === 'verify' && !success && (
          <form onSubmit={(e) => { e.preventDefault(); submitReset(); }}>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 24 }}>
              کد ۷ رقمی ارسال‌شده به موبایل و رمز عبور جدید را وارد کنید
            </p>
            <div style={{ marginBottom: 20, background: '#fff', opacity: 1 }}>
              <OtpInput
                value={code}
                onChange={setCode}
                numInputs={CODE_LEN}
                shouldAutoFocus
                renderInput={(props) => <input {...props} disabled={loading || success} />}
                inputStyle={{ width: '2.2em', height: '2.4em', margin: '0 4px', fontSize: 18, borderRadius: 8, border: '1px solid #e2e8f0', textAlign: 'center', background: '#fff', backgroundColor: '#fff', opacity: 1, color: '#0f172a' }}
                containerStyle={{ direction: 'ltr', background: '#fff', opacity: 1 }}
              />
            </div>
            <input
              type="password"
              placeholder="رمز عبور جدید"
              value={newPass}
              dir="ltr"
              className="pj-ltr-field"
              onChange={(e) => setNewPass(e.target.value)}
              style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }}
            />
            <div style={{ textAlign: 'right', marginBottom: 12 }}><PasswordStrengthMeter password={newPass} /></div>
            <input
              type="password"
              placeholder="تکرار رمز عبور جدید"
              value={confirmPass}
              dir="ltr"
              className="pj-ltr-field"
              onChange={(e) => setConfirmPass(e.target.value)}
              style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: `1px solid ${passwordsMismatch ? '#fca5a5' : '#e2e8f0'}`, fontSize: 14, marginBottom: passwordsMismatch ? 6 : 20, fontFamily: 'inherit', boxSizing: 'border-box' }}
            />
            {passwordsMismatch && (
              <p style={{ color: '#dc2626', fontSize: 12, textAlign: 'right', margin: '0 0 14px' }}>رمز عبور و تکرار آن یکسان نیستند.</p>
            )}
            <button
              type="submit"
              disabled={loading || code.length < CODE_LEN || passwordsMismatch || !newPass || !confirmPass}
              style={{ width: '100%', padding: '12px', borderRadius: 10, background: 'linear-gradient(135deg,#29AAE1,#233A7A)', color: '#fff', fontSize: 14, fontWeight: 700, border: 'none', cursor: (code.length < CODE_LEN || passwordsMismatch || !newPass || !confirmPass) ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 16, opacity: (code.length < CODE_LEN || passwordsMismatch || !newPass || !confirmPass) ? 0.6 : 1 }}
            >
              {loading ? <><Spinner size={14} color="#fff" /> در حال تأیید...</> : 'تغییر رمز عبور'}
            </button>
            <button
              type="button"
              onClick={resendCode}
              disabled={countdown > 0 || sending}
              style={{ background: 'none', border: 'none', fontSize: 13, cursor: countdown > 0 ? 'default' : 'pointer', fontFamily: 'inherit', color: countdown > 0 ? '#94a3b8' : '#29AAE1' }}
            >
              {sending ? 'در حال ارسال...' : countdown > 0 ? `ارسال مجدد تا ${toPersian(countdown)} ثانیه دیگر` : 'ارسال مجدد کد'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export const ResetPasswordPage = () => {
  return (<>
  </>)
}